import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Mail, ArrowLeft } from 'lucide-react'
import api from '../services/api'

const ForgotPassword = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [isResendingOTP, setIsResendingOTP] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [step, setStep] = useState(1) // 1: Enter email, 2: Enter OTP, 3: Reset password
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm()

    const handleResendOTP = async () => {
        setIsResendingOTP(true)
        setError('')
        
        try {
            console.log('Resending forgot password OTP to:', email)
            const response = await api.post('/auth/resend-forgot-password-otp', { email })
            console.log('Resend forgot password OTP response:', response.data)
            
            setSuccess('Password reset OTP has been resent to your email')
        } catch (error) {
            console.error('Resend forgot password OTP error:', error)
            
            // Handle specific error cases with meaningful messages
            if (error.code === 'ERR_NETWORK') {
                setError('Network connection error. Please check your internet connection and try again.')
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message || 'Invalid request. Please check your email address.')
            } else if (error.response?.status === 404) {
                setError('Service temporarily unavailable. Please try again later or contact support.')
            } else if (error.response?.status === 429) {
                setError(error.response?.data?.message || 'Too many requests. Please wait before trying again.')
            } else if (error.response?.status === 500) {
                setError(error.response?.data?.message || 'Server error. Please try again in a few minutes.')
            } else {
                setError(error.response?.data?.message || 'Failed to resend OTP. Please try again.')
            }
        } finally {
            setIsResendingOTP(false)
        }
    }

    const onSubmit = async (data) => {
        setIsLoading(true)
        setError('')
        setSuccess('')

        try {
            if (step === 1) {
                // Step 1: Send OTP
                const response = await api.post('/auth/forgot-password', data)
                setEmail(data.email)
                setSuccess(response.data.message)
                setStep(2)
            } else if (step === 2) {
                // Step 2: Verify OTP
                const response = await api.post('/auth/verify-reset-otp', {
                    email: email,
                    otp: data.otp
                })
                setOtp(data.otp)
                setSuccess(response.data.message)
                setStep(3)
            } else if (step === 3) {
                // Step 3: Reset password
                if (data.newPassword !== data.confirmPassword) {
                    setError('Passwords do not match')
                    return
                }
                const response = await api.post('/auth/reset-password', {
                    email: email,
                    otp: otp,
                    newPassword: data.newPassword
                })
                setSuccess(response.data.message)
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    window.location.href = '/login'
                }, 2000)
            }
        } catch (error) {
            console.error('Forgot password error:', error)
            
            // Handle specific error cases with meaningful messages
            if (error.code === 'ERR_NETWORK') {
                setError('Network connection error. Please check your internet connection and try again.')
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message || 'Invalid request. Please check your information and try again.')
            } else if (error.response?.status === 404) {
                setError('Service temporarily unavailable. Please try again later or contact support.')
            } else if (error.response?.status === 429) {
                setError(error.response?.data?.message || 'Too many requests. Please wait before trying again.')
            } else if (error.response?.status === 500) {
                setError(error.response?.data?.message || 'Server error. Please try again in a few minutes.')
            } else {
                setError(error.response?.data?.message || 'An unexpected error occurred. Please try again.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex">
            {/* Left side - Brand section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-700 via-indigo-800 to-cyan-700 p-8">
                <div className="flex flex-col justify-center items-center text-center text-white w-full">
                    <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-20 mb-4" />
                    <p className="text-lg text-cyan-200 mb-4">SHAR Projects Pvt. Ltd</p>
                    <p className="text-xl text-cyan-100 font-medium">Camera Management System</p>
                </div>
            </div>

            {/* Right side - Forgot password form */}
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100 py-8 px-4">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex flex-col items-center mb-8 lg:hidden text-center">
                        <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-14 mb-2" />
                        <p className="text-sm text-gray-600">SHAR Projects Pvt. Ltd</p>
                        <p className="text-sm text-gray-500">Camera Management System</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {step === 1 && 'Forgot Password?'}
                                {step === 2 && 'Enter OTP'}
                                {step === 3 && 'Reset Password'}
                            </h2>
                            <p className="text-gray-600">
                                {step === 1 && 'Enter your email to receive a reset OTP'}
                                {step === 2 && 'Enter the 6-digit OTP sent to your email'}
                                {step === 3 && 'Enter your new password'}
                            </p>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Success message */}
                        {success && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-600">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {step === 1 && (
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            id="email"
                                            type="email"
                                            autoComplete="email"
                                            className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Enter your email"
                                            {...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: 'Invalid email address'
                                                }
                                            })}
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                    )}
                                </div>
                            )}

                            {step === 2 && (
                                <>
                                    <div>
                                        <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                                            OTP Code
                                        </label>
                                        <input
                                            id="otp"
                                            type="text"
                                            maxLength="6"
                                            className="input text-center text-2xl font-mono focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="000000"
                                            {...register('otp', {
                                                required: 'OTP is required',
                                                pattern: {
                                                    value: /^[0-9]{6}$/,
                                                    message: 'OTP must be 6 digits'
                                                }
                                            })}
                                        />
                                        {errors.otp && (
                                            <p className="mt-1 text-sm text-red-600">{errors.otp.message}</p>
                                        )}
                                    </div>

                                    <div className="text-center">
                                        <button
                                            type="button"
                                            onClick={handleResendOTP}
                                            disabled={isResendingOTP}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-cyan-600 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100 hover:border-cyan-300 hover:text-cyan-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                                        >
                                            {isResendingOTP ? (
                                                <span className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyan-600 mr-2"></div>
                                                    Resending...
                                                </span>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    Didn't receive OTP? Resend
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    <div>
                                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                            New Password
                                        </label>
                                        <input
                                            id="newPassword"
                                            type="password"
                                            className="input focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Enter new password"
                                            {...register('newPassword', {
                                                required: 'New password is required',
                                                minLength: {
                                                    value: 6,
                                                    message: 'Password must be at least 6 characters'
                                                }
                                            })}
                                        />
                                        {errors.newPassword && (
                                            <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                            Confirm Password
                                        </label>
                                        <input
                                            id="confirmPassword"
                                            type="password"
                                            className="input focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Confirm new password"
                                            {...register('confirmPassword', {
                                                required: 'Please confirm your password'
                                            })}
                                        />
                                        {errors.confirmPassword && (
                                            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {step === 1 && 'Sending OTP...'}
                                            {step === 2 && 'Verifying OTP...'}
                                            {step === 3 && 'Resetting Password...'}
                                        </div>
                                    ) : (
                                        <>
                                            {step === 1 && 'Send OTP'}
                                            {step === 2 && 'Verify OTP'}
                                            {step === 3 && 'Reset Password'}
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="text-center">
                                {step === 1 ? (
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center text-sm text-cyan-600 hover:text-cyan-500 transition"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Back to login
                                    </Link>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setStep(step - 1)}
                                        className="inline-flex items-center text-sm text-cyan-600 hover:text-cyan-500 transition"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Go back
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ForgotPassword 